/**
 * BusinessEssentialsBuilder Modal
 * 
 * Generates 4 business assets in one workflow:
 * - Social Media Bio
 * - Business Description
 * - Offer Summary
 * - Pricing Calculator
 * 
 * Output varies by plan tier:
 * - Starter: Text + DIY instructions
 * - Growth: Editable templates
 * - Pro: Done-for-you + PDF download
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Sparkle, Lightning, CheckCircle, Copy, Download, 
  ArrowRight, Buildings, CurrencyCircleDollar, UserCircle,
  Spinner, Info, CaretDown, CaretUp, TextAlignLeft, Calculator, ArrowUpRight, Lock
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { isTrialUser, canDownload, getUpgradeMessage, getEffectiveTier } from '@/utils/trialAccess';
import { toast } from 'sonner';
import api from '@/utils/api';
import { downloadBlob as safeDownloadBlob } from '@/utils/downloadBlob';
import useBusinessMemoryPrefill from '@/hooks/useBusinessMemoryPrefill';
import useFoundationAutoStart from '@/hooks/useFoundationAutoStart';
import useFoundationAutoRoute from '@/hooks/useFoundationAutoRoute';
import { useMissionHandoff } from '@/store/missionHandoffContext';
import { normalizeBuilderAsset } from '@/utils/normalizeBuilderAsset';
import BusinessMemoryBanner from './BusinessMemoryBanner';
import NextStepGuide from './NextStepGuide';

const TIER_BADGES = {
  starter: { label: 'Starter', color: 'bg-slate-600', description: 'Text + DIY Instructions' },
  growth: { label: 'Growth', color: 'bg-blue-600', description: 'Editable Templates' },
  pro: { label: 'Pro', color: 'bg-purple-600', description: 'Done-For-You + PDF' }
};

const ASSET_SECTIONS = [
  { id: 'social_media_bio', name: 'Social Media Bio', icon: UserCircle, color: 'text-pink-400' },
  { id: 'business_description', name: 'Business Description', icon: Buildings, color: 'text-blue-400' },
  { id: 'offer_summary', name: 'Offer Summary', icon: TextAlignLeft, color: 'text-emerald-400' },
  { id: 'pricing_calculator', name: 'Pricing Calculator', icon: Calculator, color: 'text-amber-400' }
];

export default function BusinessEssentialsBuilder({ isOpen, onClose, onNext, onPickStep, onSuccess, user }) {
  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [businessDescription, setBusinessDescription] = useState('');
  const [targetCustomer, setTargetCustomer] = useState('');
  const [offer, setOffer] = useState('');
  const [price, setPrice] = useState('');
  const [callToAction, setCallToAction] = useState('Get Started');
  const [detectedBusiness, setDetectedBusiness] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Round 108 — mission handoff prefill.
  const { handoff: missionHandoff, pickField: pickMissionField } = useMissionHandoff();
  const isMissionMode = !!(missionHandoff && missionHandoff.asset_id
    && (missionHandoff.asset_type === 'business_profile' || !missionHandoff.asset_type));

  useEffect(() => {
    if (!isOpen || !isMissionMode) return;
    const missionDesc = pickMissionField('summary', 'body', 'pitch');
    if (missionDesc && typeof missionDesc === 'string') setBusinessDescription(missionDesc);
    const missionCustomer = pickMissionField('target_audience', 'recommended_audience');
    if (missionCustomer && typeof missionCustomer === 'string') setTargetCustomer(missionCustomer);
    const missionOffer = pickMissionField('headline', 'pitch', 'title');
    if (missionOffer && typeof missionOffer === 'string') setOffer(missionOffer);
    const missionPrice = pickMissionField('price');
    if (missionPrice && typeof missionPrice === 'string') setPrice(missionPrice);
    const missionCta = pickMissionField('cta', 'cta_copy');
    if (missionCta && typeof missionCta === 'string') setCallToAction(missionCta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMissionMode]);
  const [copied, setCopied] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  // Determine effective plan tier (uses simulation-aware function)
  const effectiveTier = getEffectiveTier(user);
  const tierInfo = TIER_BADGES[effectiveTier] || TIER_BADGES.starter;
  
  // Check if user is on trial (simulation-aware)
  const isTrial = isTrialUser(user);
  const canUserDownload = canDownload(user);

  // Prefill fields from the user's saved business memory when the modal opens.
  // The user's own input always wins — prefill only fires when a field is empty.
  useBusinessMemoryPrefill(isOpen, {
    businessDescription: [businessDescription, setBusinessDescription],
    targetCustomer: [targetCustomer, setTargetCustomer],
    offer: [offer, setOffer],
    price: [price, setPrice],
  });

  // Round 218A Phase 8D-3D — when Business Brain + Brand Brain are
  // complete AND memory prefill has populated the required inputs,
  // skip the input form entirely and start generation. The customer
  // never re-answers "Who is your target customer?" for information
  // already in Business Brain.
  useFoundationAutoStart({
    isOpen,
    ready: !!(businessDescription && targetCustomer && offer),
    step,
    onStart: () => handleGenerate(),
  });

  // Round 218A P0 — foundation gate.
  useFoundationAutoRoute({ isOpen, onClose });

  // Auto-detect business type as user types
  useEffect(() => {
    const detectTimeout = setTimeout(async () => {
      const combined = `${businessDescription} ${offer} ${targetCustomer}`.trim();
      if (combined.length >= 10) {
        try {
          const response = await api.post('/business-essentials/detect-business', {
            business_description: businessDescription,
            offer: offer,
            target_customer: targetCustomer
          });
          setDetectedBusiness(response.data);
        } catch (error) {
          // Silent fail for preview
        }
      }
    }, 500);

    return () => clearTimeout(detectTimeout);
  }, [businessDescription, offer, targetCustomer]);

  const handleGenerate = async () => {
    if (!businessDescription.trim()) {
      toast.error('Please describe your business');
      return;
    }

    setLoading(true);
    setStep('generating');

    try {
      const response = await api.post('/business-essentials/generate', {
        business_description: businessDescription,
        target_customer: targetCustomer || 'ideal customers',
        offer: offer || 'my services',
        price: price || '$100',
        call_to_action: callToAction || 'Get Started'
      });

      setResult(response.data);
      setStep('result');
      toast.success('Business essentials created!');
      try { if (onSuccess) onSuccess({ meta: normalizeBuilderAsset(response.data) }); } catch (_) { /* noop */ }
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate business essentials');
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    // Block copy for trial users
    if (isTrial) {
      setUpgradeMessage('Upgrade to copy full content. Trial users get preview only.');
      setShowUpgradePrompt(true);
      return;
    }
    navigator.clipboard.writeText(typeof text === 'object' ? JSON.stringify(text, null, 2) : text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyAll = () => {
    // Block copy for trial users
    if (isTrial) {
      setUpgradeMessage('Upgrade to copy full content. Trial users get preview only.');
      setShowUpgradePrompt(true);
      return;
    }
    if (!result?.content) return;
    
    const allText = `
=== SOCIAL MEDIA BIO ===
Headline: ${result.content.social_media_bio?.headline || ''}
Description: ${result.content.social_media_bio?.description || ''}
CTA: ${result.content.social_media_bio?.cta || ''}
Link Text: ${result.content.social_media_bio?.link_text || ''}

=== BUSINESS DESCRIPTION ===
${result.content.business_description?.short_description || ''}
Mission: ${result.content.business_description?.mission_statement || ''}
Value: ${result.content.business_description?.value_statement || ''}

=== OFFER SUMMARY ===
What You Sell: ${result.content.offer_summary?.what_you_sell || ''}
Who It's For: ${result.content.offer_summary?.who_its_for || ''}
Benefits: ${(result.content.offer_summary?.key_benefits || []).join(', ')}
Price: ${result.content.offer_summary?.price_explanation || ''}

=== PRICING CALCULATOR ===
Recommended Price: ${result.content.pricing_calculator?.recommended_price || ''}
Monthly Revenue: ${result.content.pricing_calculator?.monthly_revenue_example || ''}
Break-Even: ${result.content.pricing_calculator?.break_even_estimate || ''}
Profit Example: ${result.content.pricing_calculator?.profit_example || ''}
`.trim();
    
    navigator.clipboard.writeText(allText);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPDF = async () => {
    // Check trial access
    if (!canUserDownload) {
      setUpgradeMessage(getUpgradeMessage('download_pdf'));
      setShowUpgradePrompt(true);
      return;
    }
    
    if (!result?.id || !result?.has_pdf) {
      toast.error('PDF not available');
      return;
    }

    try {
      const response = await api.get(`/business-essentials/${result.id}/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      await safeDownloadBlob(blob, `business_essentials_${result.detected_business_type || 'pack'}.pdf`);
      toast.success('PDF Downloaded!');
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleReset = () => {
    setStep('input');
    setBusinessDescription('');
    setTargetCustomer('');
    setOffer('');
    setPrice('');
    setCallToAction('Get Started');
    setDetectedBusiness(null);
    setResult(null);
    setExpandedSection(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  // Render asset content
  const renderAssetContent = (assetId, data) => {
    if (!data) return null;
    
    switch (assetId) {
      case 'social_media_bio':
        return (
          <div className="space-y-3">
            <div>
              <span className="text-slate-400 text-xs font-medium">HEADLINE</span>
              <p className="text-white text-sm font-medium">{data.headline}</p>
            </div>
            <div>
              <span className="text-slate-400 text-xs font-medium">DESCRIPTION</span>
              <p className="text-slate-300 text-sm">{data.description}</p>
            </div>
            <div className="flex space-x-4">
              <div>
                <span className="text-slate-400 text-xs font-medium">CTA</span>
                <p className="text-pink-400 text-sm font-medium">{data.cta}</p>
              </div>
              <div>
                <span className="text-slate-400 text-xs font-medium">LINK TEXT</span>
                <p className="text-blue-400 text-sm">{data.link_text}</p>
              </div>
            </div>
          </div>
        );
      
      case 'business_description':
        return (
          <div className="space-y-3">
            <div>
              <span className="text-slate-400 text-xs font-medium">SHORT DESCRIPTION</span>
              <p className="text-slate-300 text-sm">{data.short_description}</p>
            </div>
            <div>
              <span className="text-slate-400 text-xs font-medium">MISSION</span>
              <p className="text-blue-400 text-sm">{data.mission_statement}</p>
            </div>
            <div>
              <span className="text-slate-400 text-xs font-medium">VALUE PROPOSITION</span>
              <p className="text-emerald-400 text-sm">{data.value_statement}</p>
            </div>
          </div>
        );
      
      case 'offer_summary':
        return (
          <div className="space-y-3">
            <div>
              <span className="text-slate-400 text-xs font-medium">WHAT YOU SELL</span>
              <p className="text-white text-sm">{data.what_you_sell}</p>
            </div>
            <div>
              <span className="text-slate-400 text-xs font-medium">WHO IT'S FOR</span>
              <p className="text-slate-300 text-sm">{data.who_its_for}</p>
            </div>
            <div>
              <span className="text-slate-400 text-xs font-medium">KEY BENEFITS</span>
              <ul className="mt-1 space-y-1">
                {(data.key_benefits || []).map((benefit, i) => (
                  <li key={i} className="flex items-center space-x-2 text-sm text-emerald-400">
                    <CheckCircle size={12} weight="fill" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <span className="text-slate-400 text-xs font-medium">PRICE EXPLANATION</span>
              <p className="text-amber-400 text-sm">{data.price_explanation}</p>
            </div>
          </div>
        );
      
      case 'pricing_calculator':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-500/10 rounded-lg p-3">
                <span className="text-slate-400 text-xs font-medium">RECOMMENDED PRICE</span>
                <p className="text-emerald-400 text-lg font-bold">{data.recommended_price}</p>
              </div>
              <div className="bg-blue-500/10 rounded-lg p-3">
                <span className="text-slate-400 text-xs font-medium">BREAK-EVEN</span>
                <p className="text-blue-400 text-sm">{data.break_even_estimate}</p>
              </div>
            </div>
            <div>
              <span className="text-slate-400 text-xs font-medium">MONTHLY REVENUE EXAMPLE</span>
              <p className="text-white text-sm">{data.monthly_revenue_example}</p>
            </div>
            <div>
              <span className="text-slate-400 text-xs font-medium">PROFIT EXAMPLE</span>
              <p className="text-amber-400 text-sm">{data.profit_example}</p>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/10 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="business-essentials-builder-modal"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Buildings size={20} weight="fill" className="text-white" />
              </div>
              <div>
                <h2 className="text-white text-lg font-bold">Business Essentials</h2>
                <div className="flex items-center space-x-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${tierInfo.color} text-white`}>
                    {tierInfo.label}
                  </span>
                  <span className="text-slate-400 text-xs">{tierInfo.description}</span>
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="close-business-essentials-modal"
            >
              <X size={20} className="text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <AnimatePresence mode="wait">
              {/* INPUT STEP */}
              {step === 'input' && (
                <motion.div
                  key="input"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  <BusinessMemoryBanner />
                  {/* Business Description */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      What does your business do? <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={businessDescription}
                      onChange={(e) => setBusinessDescription(e.target.value)}
                      placeholder="e.g., I help small business owners grow their online presence through social media management..."
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 resize-none"
                      rows={2}
                      data-testid="business-description-input"
                    />
                    
                    {/* Auto-detected business type */}
                    {detectedBusiness && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 flex items-center space-x-2 text-sm"
                      >
                        <Sparkle size={14} className="text-emerald-400" weight="fill" />
                        <span className="text-slate-400">Detected:</span>
                        <span className="text-emerald-400 font-medium">{detectedBusiness.detected_name}</span>
                      </motion.div>
                    )}
                  </div>

                  {/* Target Customer */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Who is your target customer?
                    </label>
                    <input
                      type="text"
                      value={targetCustomer}
                      onChange={(e) => setTargetCustomer(e.target.value)}
                      placeholder="e.g., Small business owners who want to grow on social media"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50"
                      data-testid="target-customer-input"
                    />
                  </div>

                  {/* Offer and Price Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Your Main Offer
                      </label>
                      <input
                        type="text"
                        value={offer}
                        onChange={(e) => setOffer(e.target.value)}
                        placeholder="e.g., Social media management"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50"
                        data-testid="offer-input"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Starting Price
                      </label>
                      <input
                        type="text"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="e.g., $500/month"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50"
                        data-testid="price-input"
                      />
                    </div>
                  </div>

                  {/* CTA */}
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Call to Action
                    </label>
                    <input
                      type="text"
                      value={callToAction}
                      onChange={(e) => setCallToAction(e.target.value)}
                      placeholder="e.g., Book a Free Call"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50"
                      data-testid="cta-input"
                    />
                  </div>

                  {/* Assets Preview */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Buildings size={16} className="text-emerald-400" />
                      <span className="text-white text-sm font-medium">4 Assets in One Build</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {ASSET_SECTIONS.map((asset) => {
                        const Icon = asset.icon;
                        return (
                          <div key={asset.id} className="flex items-center space-x-2 bg-white/5 rounded-lg px-3 py-2">
                            <Icon size={14} className={asset.color} weight="fill" />
                            <span className="text-slate-300 text-xs">{asset.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Tier Preview */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Info size={16} className="text-emerald-400" />
                      <span className="text-white text-sm font-medium">What you'll get ({tierInfo.label})</span>
                    </div>
                    {effectiveTier === 'starter' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Complete text for all 4 assets</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Step-by-step setup instructions</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Recommended tools list</span>
                        </li>
                      </ul>
                    )}
                    {effectiveTier === 'growth' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Editable templates with [customizable] sections</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Multiple variations to choose from</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Customization guide</span>
                        </li>
                      </ul>
                    )}
                    {effectiveTier === 'pro' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Finished, professional assets</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Download as PDF</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Ready to copy/paste anywhere</span>
                        </li>
                      </ul>
                    )}
                  </div>

                  {/* Generate Button */}
                  <button
                    onClick={handleGenerate}
                    disabled={!businessDescription.trim() || loading}
                    className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-xl flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    data-testid="generate-business-essentials-btn"
                  >
                    <Buildings size={20} weight="fill" />
                    <span>Generate All 4 Assets</span>
                    <ArrowRight size={18} weight="bold" />
                  </button>
                </motion.div>
              )}

              {/* GENERATING STEP */}
              {step === 'generating' && (
                <motion.div
                  key="generating"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="py-12 text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Spinner size={32} className="text-white animate-spin" />
                  </div>
                  <h3 className="text-white text-lg font-bold mb-2">Creating Your Business Essentials</h3>
                  <p className="text-slate-400 text-sm">
                    Generating 4 assets in one go...
                  </p>
                  <div className="mt-6 space-y-2 max-w-xs mx-auto">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 8 }}
                      className="h-1 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full"
                    />
                  </div>
                </motion.div>
              )}

              {/* RESULT STEP */}
              {step === 'result' && result && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-5"
                >
                  {/* Success Header */}
                  <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle size={24} className="text-emerald-400" weight="fill" />
                      <div>
                        <h3 className="text-white font-bold">Business Essentials Created!</h3>
                        <p className="text-emerald-400 text-sm">4 assets saved to My Creations</p>
                      </div>
                    </div>
                  </div>

                  {/* Business Info */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <h4 className="text-white text-lg font-bold mb-1">
                      {result.detected_business_name} Business
                    </h4>
                    <div className="flex items-center space-x-3 text-xs text-slate-400">
                      <span>Type: <span className="text-emerald-400">{result.detected_business_type?.replace('_', ' ')}</span></span>
                      <span>Tier: <span className="text-purple-400">{result.plan_tier}</span></span>
                    </div>
                  </div>

                  {/* Pro Download Buttons - HIDDEN for trial users */}
                  {result.formatted_output?.output_type === 'done_for_you' && !isTrial && (
                    <div className="flex space-x-3">
                      <button
                        onClick={handleDownloadPDF}
                        disabled={!result?.has_pdf}
                        className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="download-pdf-btn"
                      >
                        <Download size={18} weight="bold" />
                        <span>Download PDF</span>
                      </button>
                      <button
                        onClick={handleCopyAll}
                        className="py-3 px-4 bg-white/10 hover:bg-white/20 text-slate-300 font-medium rounded-xl flex items-center justify-center space-x-2 transition-all"
                      >
                        <Copy size={16} />
                        <span>Copy All</span>
                      </button>
                    </div>
                  )}
                  
                  {/* Trial user sees upgrade message instead of downloads */}
                  {result.formatted_output?.output_type === 'done_for_you' && isTrial && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                      <p className="text-red-400 font-medium mb-2">Downloads Locked</p>
                      <p className="text-slate-400 text-sm mb-3">Upgrade to Pro to download PDF and copy all assets.</p>
                      <Link 
                        to="/pricing" 
                        className="inline-block px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700"
                      >
                        Upgrade Now
                      </Link>
                    </div>
                  )}

                  {/* Starter/Growth Copy All Button - HIDDEN for trial users */}
                  {result.formatted_output?.output_type !== 'done_for_you' && !isTrial && (
                    <button
                      onClick={handleCopyAll}
                      className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl flex items-center justify-center space-x-2 transition-all"
                    >
                      <Copy size={16} />
                      <span>Copy All Assets</span>
                    </button>
                  )}
                  
                  {/* Trial user sees upgrade message instead of copy button */}
                  {result.formatted_output?.output_type !== 'done_for_you' && isTrial && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-center">
                      <p className="text-amber-400 text-sm">Upgrade to copy all assets</p>
                    </div>
                  )}

                  {/* Asset Sections */}
                  <div className="space-y-3">
                    {ASSET_SECTIONS.map((asset) => {
                      const Icon = asset.icon;
                      const isExpanded = expandedSection === asset.id;
                      const assetData = result.content?.[asset.id];
                      
                      return (
                        <div 
                          key={asset.id}
                          className="bg-white/5 border border-white/10 rounded-xl overflow-hidden"
                        >
                          {/* Asset Header */}
                          <div
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors cursor-pointer"
                            onClick={() => setExpandedSection(isExpanded ? null : asset.id)}
                          >
                            <div className="flex items-center space-x-3">
                              <span className={`w-8 h-8 rounded-full bg-white/10 flex items-center justify-center ${asset.color}`}>
                                <Icon size={16} weight="fill" />
                              </span>
                              <span className="text-white text-sm font-medium">{asset.name}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopy(assetData);
                                }}
                                className="p-1.5 hover:bg-white/10 rounded-lg"
                              >
                                <Copy size={14} className="text-slate-400" />
                              </button>
                              {isExpanded ? (
                                <CaretUp size={16} className="text-slate-400" />
                              ) : (
                                <CaretDown size={16} className="text-slate-400" />
                              )}
                            </div>
                          </div>

                          {/* Asset Content (Expanded) */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="border-t border-white/10"
                              >
                                <div className="p-4">
                                  {renderAssetContent(asset.id, assetData)}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>

                  {/* DIY Instructions for Starter */}
                  {result.formatted_output?.output_type === 'text_with_instructions' && (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                      <h5 className="text-white font-medium mb-3 flex items-center space-x-2">
                        <Lightning size={16} className="text-amber-400" />
                        <span>How to Use Your Assets</span>
                      </h5>
                      <div className="space-y-3">
                        {Object.entries(result.formatted_output.diy_instructions || {}).map(([key, instructions]) => (
                          <div key={key}>
                            <p className="text-slate-400 text-xs font-medium uppercase mb-1">
                              {key.replace(/_/g, ' ')}
                            </p>
                            <ul className="space-y-1">
                              {(instructions || []).slice(0, 3).map((inst, i) => (
                                <li key={i} className="text-slate-300 text-sm flex items-start space-x-2">
                                  <span className="text-emerald-400">•</span>
                                  <span>{inst}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upgrade Prompt for Non-Pro Users */}
                  {result?.delivery_mode !== 'done_for_you' && effectiveTier !== 'pro' && (
                    <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4" data-testid="upgrade-prompt">
                      <p className="text-white text-sm font-medium mb-2">
                        {effectiveTier === 'starter' || effectiveTier === 'free' || effectiveTier === 'trial'
                          ? 'Want this finished and ready to use?'
                          : 'Want this fully built and ready to download?'
                        }
                      </p>
                      <p className="text-slate-400 text-xs mb-3">
                        {effectiveTier === 'starter' || effectiveTier === 'free' || effectiveTier === 'trial'
                          ? 'Upgrade to Pro to get done-for-you assets.'
                          : 'Upgrade to Pro.'
                        }
                      </p>
                      <Link
                        to="/pricing"
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-all"
                        data-testid="upgrade-btn"
                      >
                        <span>Upgrade</span>
                        <ArrowUpRight size={16} weight="bold" />
                      </Link>
                    </div>
                  )}

                  {/* Next Step Guide — replaces the old Create Another / Done buttons.
                      User stays inside the build loop; onPickStep opens the requested
                      builder; onClose is the soft exit. */}
                  <NextStepGuide
                    currentStep="essentials"
                    onPick={(stepKey) => {
                      handleReset();
                      onClose();
                      if (onPickStep) onPickStep(stepKey);
                      else if (onNext && stepKey === 'ebook') onNext();
                    }}
                    onDone={handleClose}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
      
      {/* Trial Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setShowUpgradePrompt(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 max-w-md w-full border border-purple-500/30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock size={32} weight="fill" className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Upgrade to Continue</h3>
              <p className="text-slate-400 mb-6">{upgradeMessage}</p>
              <div className="space-y-3">
                <Link
                  to="/pricing"
                  className="block w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl transition-all"
                >
                  View Plans
                </Link>
                <button
                  onClick={() => setShowUpgradePrompt(false)}
                  className="w-full py-3 bg-white/10 hover:bg-white/20 text-slate-300 font-medium rounded-xl transition-colors"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
